// SPDX-License-Identifier: AGPL-3.0-or-later

import * as UserSettingsCommands from '@app/features/user/commands/UserSettingsCommands';
import {buildDraftStatus} from '@app/features/user/state/CustomStatus';
import {makeAutoObservable} from 'mobx';

export type ActivityType = 'playing' | 'listening' | 'streaming' | 'watching' | 'competing';

export interface ActivityData {
	type: ActivityType;
	name: string;
	details?: string;
	state?: string;
	startedAt?: number;
	showTime?: boolean;
}

export interface ActivityPreset {
	id: string;
	name: string;
	type: ActivityType;
	defaultDetails?: string;
	defaultState?: string;
	icon: string;
}

export const ACTIVITY_PRESETS: ReadonlyArray<ActivityPreset> = [
	{
		id: 'minecraft',
		name: 'Minecraft',
		type: 'playing',
		defaultDetails: 'Survival Mode',
		defaultState: 'Exploring Overworld',
		icon: 'cube',
	},
	{
		id: 'spotify',
		name: 'Spotify',
		type: 'listening',
		defaultDetails: 'Listening to Music',
		defaultState: 'Playing Favorites',
		icon: 'spotify',
	},
	{
		id: 'cs2',
		name: 'Counter-Strike 2',
		type: 'playing',
		defaultDetails: 'Premier Match',
		defaultState: 'Dust II (Competitive)',
		icon: 'crosshair',
	},
	{
		id: 'valorant',
		name: 'Valorant',
		type: 'playing',
		defaultDetails: 'Competitive',
		defaultState: 'Ascent (In Game)',
		icon: 'game',
	},
	{
		id: 'vscode',
		name: 'Visual Studio Code',
		type: 'playing',
		defaultDetails: 'Workspace: ND-Chat',
		defaultState: 'Building modern chat platform',
		icon: 'code',
	},
	{
		id: 'roblox',
		name: 'Roblox',
		type: 'playing',
		defaultDetails: 'Playing with friends',
		defaultState: 'In Experience',
		icon: 'game',
	},
	{
		id: 'fortnite',
		name: 'Fortnite',
		type: 'playing',
		defaultDetails: 'Battle Royale',
		defaultState: 'Top 10 Remaining',
		icon: 'game',
	},
	{
		id: 'lol',
		name: 'League of Legends',
		type: 'competing',
		defaultDetails: 'Ranked Solo/Duo',
		defaultState: "Summoner's Rift",
		icon: 'trophy',
	},
	{
		id: 'youtube',
		name: 'YouTube',
		type: 'watching',
		defaultDetails: 'Watching Tech Videos',
		defaultState: 'In Fullscreen',
		icon: 'youtube',
	},
	{
		id: 'twitch',
		name: 'Twitch',
		type: 'streaming',
		defaultDetails: 'Live Stream',
		defaultState: 'Chatting with Community',
		icon: 'twitch',
	},
];

export function getActivityHeader(type: ActivityType): string {
	switch (type) {
		case 'playing':
			return 'PLAYING A GAME';
		case 'listening':
			return 'LISTENING TO SPOTIFY / MUSIC';
		case 'streaming':
			return 'LIVE ON STREAM';
		case 'watching':
			return 'WATCHING MEDIA';
		case 'competing':
			return 'COMPETING IN';
	}
}

export function getActivityTypeLabel(type: ActivityType): string {
	switch (type) {
		case 'playing':
			return 'Playing';
		case 'listening':
			return 'Listening to';
		case 'streaming':
			return 'Streaming';
		case 'watching':
			return 'Watching';
		case 'competing':
			return 'Competing in';
	}
}

export function getActivityEmoji(type: ActivityType): string {
	switch (type) {
		case 'playing':
			return '🎮';
		case 'listening':
			return '🎧';
		case 'streaming':
			return '🟣';
		case 'watching':
			return '📺';
		case 'competing':
			return '🏆';
	}
}

export function formatActivityStatusText(activity: ActivityData): string {
	return `${getActivityTypeLabel(activity.type)} ${activity.name}`;
}

export function formatElapsedTime(startedAt: number): string {
	const elapsedSeconds = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
	const hours = Math.floor(elapsedSeconds / 3600);
	const minutes = Math.floor((elapsedSeconds % 3600) / 60);
	const seconds = elapsedSeconds % 60;
	const pad = (n: number) => n.toString().padStart(2, '0');
	if (hours > 0) {
		return `${pad(hours)}:${pad(minutes)}:${pad(seconds)} elapsed`;
	}
	return `${pad(minutes)}:${pad(seconds)} elapsed`;
}

export function parseActivityFromStatus(
	customStatus?: {text?: string | null; emojiName?: string | null} | null,
): ActivityData | null {
	if (!customStatus?.text) return null;
	const text = customStatus.text.trim();
	const match = text.match(/^(?:[🎮🎧🟣📺🏆📡]\s*)?(Playing|Listening to|Streaming|Watching|Competing in)\s+(.+)$/i);
	if (!match) return null;
	const verb = match[1].toLowerCase();
	const name = match[2].trim();
	let type: ActivityType = 'playing';
	if (verb.startsWith('listen')) type = 'listening';
	else if (verb.startsWith('stream')) type = 'streaming';
	else if (verb.startsWith('watch')) type = 'watching';
	else if (verb.startsWith('compet')) type = 'competing';
	return {
		type,
		name,
	};
}

const STORAGE_KEY = 'fluxer_active_user_activity';

class UserActivityStore {
	currentActivity: ActivityData | null = null;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		this.loadFromStorage();
	}

	private loadFromStorage(): void {
		try {
			const saved = localStorage.getItem(STORAGE_KEY);
			if (saved) {
				const parsed = JSON.parse(saved) as ActivityData;
				if (parsed && typeof parsed.name === 'string' && parsed.type) {
					this.currentActivity = parsed;
				}
			}
		} catch {
			// ignore storage load error
		}
	}

	async setActivity(activity: ActivityData | null, syncCustomStatus: boolean = true): Promise<void> {
		this.currentActivity = activity;
		try {
			if (activity) {
				localStorage.setItem(STORAGE_KEY, JSON.stringify(activity));
			} else {
				localStorage.removeItem(STORAGE_KEY);
			}
		} catch {
			// ignore storage error
		}

		if (syncCustomStatus) {
			if (activity) {
				const emoji = getActivityEmoji(activity.type);
				const statusText = `${emoji} ${formatActivityStatusText(activity)}`;
				const statusToSave = buildDraftStatus({
					text: statusText,
					emojiId: null,
					emojiName: emoji,
					expiresAt: null,
				});
				await UserSettingsCommands.update({customStatus: statusToSave});
			} else {
				const statusToSave = buildDraftStatus({
					text: '',
					emojiId: null,
					emojiName: null,
					expiresAt: null,
				});
				await UserSettingsCommands.update({customStatus: statusToSave});
			}
		}
	}

	async clearActivity(syncCustomStatus: boolean = true): Promise<void> {
		await this.setActivity(null, syncCustomStatus);
	}

	getActivityForUser(_userId: string, customStatus?: {text?: string | null; emojiName?: string | null} | null): ActivityData | null {
		// If current user, return our rich activity state if set
		if (this.currentActivity) {
			return this.currentActivity;
		}
		// Otherwise parse from customStatus
		return parseActivityFromStatus(customStatus);
	}
}

export const userActivityStore = new UserActivityStore();
export default userActivityStore;
